import { useMemo, useState } from "react";
import { useData } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SizeMatrix from "@/components/SizeMatrix";
import { Plus, X, ChevronDown, ChevronUp } from "lucide-react";

const NONE = "__none";
const sel = "h-10 bg-[#FFFBF4] border-[#DCCFB8]";
const content = "bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118]";
const pairsOf = (l) => Object.values(l.sizes).reduce((a, b) => a + (parseInt(b) || 0), 0);

const CodeSelect = ({ p, code, codes, onCode }) => (
  <Select value={code} onValueChange={onCode}>
    <SelectTrigger data-testid={`${p}-code`} className={`${sel} h-11 flex-1 sm:max-w-xs font-mono font-bold`}>
      <SelectValue placeholder="Taban kodu seçin..." />
    </SelectTrigger>
    <SelectContent className={content}>
      {codes.length === 0 && <div className="px-3 py-2 text-sm text-[#8C7B68]">Taban kartı yok</div>}
      {codes.map((c) => <SelectItem key={c} value={c} data-testid={`${p}-code-option-${c}`}>{c}</SelectItem>)}
    </SelectContent>
  </Select>
);

const SoleColorLine = ({ p, i, l, opts, closed, onToggle, onUpdate, onRemove }) => (
  <div className="rounded-md bg-[#FFFBF4]/60 p-2" data-testid={`${p}-row-${i}`}>
    <div className="flex items-center gap-2">
      <Select value={l.colorSet ? l.color || NONE : ""} onValueChange={(v) => onUpdate(l.id, { color: v === NONE ? "" : v, colorSet: true })}>
        <SelectTrigger data-testid={`${p}-color-${i}`} className={`${sel} flex-1 sm:max-w-xs`}><SelectValue placeholder="Taban rengi seçin..." /></SelectTrigger>
        <SelectContent className={content}>
          {opts.map((c) => (
            <SelectItem key={c.id} value={c.sole_color || NONE} data-testid={`${p}-color-${i}-option-${c.sole_color || "none"}`}>{c.sole_color || "Renksiz"}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="ml-auto font-mono text-sm font-bold text-[#B45309]" data-testid={`${p}-pairs-${i}`}>{pairsOf(l)} çift</span>
      <button type="button" onClick={onToggle} title="Numaraları göster/gizle"
        data-testid={`${p}-sizes-toggle-${i}`} className="text-[#6B5B4A] hover:text-[#B45309]">
        {closed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
      </button>
      {onRemove && <button type="button" onClick={onRemove} data-testid={`${p}-row-remove-${i}`} title="Rengi kaldır" className="text-[#8C7B68] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>}
    </div>
    {!closed && (
      <div className="mt-2"><SizeMatrix values={l.sizes} testIdPrefix={`${p}-size-${i}`}
        onChange={(s, v) => onUpdate(l.id, { sizes: { ...l.sizes, [s]: v } })} /></div>
    )}
  </div>
);

// Colors still free for line l (its own color + those not taken by other lines)
const freeColors = (colors, lines, l) =>
  colors.filter((c) => (c.sole_color || "") === l.color || !lines.some((x) => x.id !== l.id && x.color === (c.sole_color || "") && x.colorSet));

// One sole code in the sole order form: code dropdown, then one line per color with its sizes
export default function SoleCodeBlock({ idx, lines, usedCodes, onCode, onUpdate, onAddColor, onRemoveLine, onRemoveBlock }) {
  const { soleCards } = useData();
  const [closed, setClosed] = useState({});
  const code = lines[0].code;
  const codes = useMemo(() => [...new Set(soleCards.map((c) => c.sole_code))].filter((c) => !usedCodes.includes(c)), [soleCards, usedCodes]);
  const colors = useMemo(() => soleCards.filter((c) => c.sole_code === code), [soleCards, code]);
  const p = `sole-block-${idx}`;

  return (
    <div className="rounded-md border border-[#DCCFB8] bg-[#EFE6D6] p-3" data-testid={p}>
      <div className="flex items-center gap-2 mb-2">
        <CodeSelect p={p} code={code} codes={codes} onCode={onCode} />
        <span className="ml-auto text-xs font-mono text-[#6B5B4A]" data-testid={`${p}-total`}>{lines.reduce((a, l) => a + pairsOf(l), 0)} çift</span>
        {onRemoveBlock && <button type="button" onClick={onRemoveBlock} data-testid={`${p}-remove`} title="Tabanı kaldır" className="text-[#8C7B68] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>}
      </div>
      {code && (
        <div className="space-y-2 mt-2">
          {lines.map((l, i) => (
            <SoleColorLine key={l.id} p={p} i={i} l={l} opts={freeColors(colors, lines, l)} closed={!!closed[l.id]}
              onToggle={() => setClosed((o) => ({ ...o, [l.id]: !o[l.id] }))} onUpdate={onUpdate}
              onRemove={lines.length > 1 ? () => onRemoveLine(l.id) : null} />
          ))}
          {lines.length < colors.length && (
            <Button type="button" variant="ghost" onClick={onAddColor} data-testid={`${p}-add-color`}
              className="h-8 px-2 text-[#B45309] hover:bg-[#FFFBF4] hover:text-[#B45309]"><Plus className="w-4 h-4 mr-1" /> Renk Ekle</Button>
          )}
        </div>
      )}
    </div>
  );
}
